import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Segment, Header, Comment, Loader, Button, Icon, Modal, Message } from 'semantic-ui-react'
import { useStore } from '../../../app/stores/store'
import { Link } from 'react-router-dom'
import { Formik, Form, Field, FieldProps } from 'formik'
import * as Yup from 'yup'
import { formatDistanceToNow } from 'date-fns'
import {
  enUS,
  zhCN,
  es,
  hi,
  ar,
  pt,
  ru,
  bn,
  fr,
  de,
  ja,
  ro,
  it,
  hu,
  el,
  tr,
  sr
} from 'date-fns/locale'
import { Game } from '../../../app/models/Game'
import { useTranslation } from 'react-i18next'

interface Props {
  game: Game
}

const COMMENT_MAX_LENGTH = 1024;

const getDateLocale = (language: string) => {
  switch (language) {
    case 'en': return enUS;
    case 'zh': return zhCN;
    case 'es': return es;
    case 'hi': return hi;
    case 'ar': return ar;
    case 'pt': return pt;
    case 'ru': return ru;
    case 'bn': return bn;
    case 'fr': return fr;
    case 'de': return de;
    case 'ja': return ja;
    case 'ur': return enUS;
    case 'ro': return ro;
    case 'it': return it;
    case 'hu': return hu;
    case 'el': return el;
    case 'tr': return tr;
    case 'sr':
    case 'srLatn': return sr;
    default: return enUS;
  }
};

const convertToLatinScript = (text: string) => {
  const cyrillicToLatin: Record<string, string> = {
    'око': 'oko', 'пре': 'pre',
    'сати': 'sati', 'сат': 'sat',
    'минута': 'minuta', 'минут': 'minut',
    'секунди': 'sekundi', 'секунд': 'sekund',
    'дан': 'dan', 'дана': 'dana',
    'месец': 'mesec', 'месеца': 'meseca',
    'година': 'godina', 'године': 'godine',
  };
  let result = text;
  Object.entries(cyrillicToLatin).forEach(([cyrillic, latin]) => {
    result = result.replace(new RegExp(cyrillic, 'g'), latin);
  });
  return result;
};

export default observer(function GameDetailedChat({ game }: Props) {
  const { commentStore, userStore } = useStore();
  const { t, i18n } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [deletingComment, setDeletingComment] = useState(false);

  useEffect(() => {
    if (game && (game.isGoing || game.isHost)) {
      commentStore.createHubConnection(game.id);
    }
    return () => {
      commentStore.clearComments();
    };
  }, [commentStore, game.id, game.isGoing]);

  const handleDeleteInitiated = (commentId: string) => {
    setCommentToDelete(commentId);
    setConfirmOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (commentToDelete) {
      setDeletingComment(true);
      try {
        await commentStore.deleteComment(commentToDelete);
      } finally {
        setDeletingComment(false);
        setConfirmOpen(false);
      }
    }
  };

  if (!game.isGoing && !game.isHost) {
    return (
      <>
        <Segment textAlign='center' attached='top' inverted color='teal' style={{ border: 'none' }}>
          <Header>{t('gameDetailedChat.title')}</Header>
        </Segment>

        <Segment attached clearing style={{ position: 'relative', minHeight: '300px' }}>
          <div style={{ filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' }}>
            <Comment.Group>
              {[1, 2, 3].map(i => (
                <Comment key={i} style={{ padding: '10px', marginBottom: '10px', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
                  <Comment.Avatar src='/assets/user.png' />
                  <Comment.Content>
                    <Comment.Author>████████</Comment.Author>
                    <Comment.Metadata><div>██ ██████ ███</div></Comment.Metadata>
                    <Comment.Text style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>
                      ████████████████████████████████████
                    </Comment.Text>
                  </Comment.Content>
                </Comment>
              ))}
            </Comment.Group>
          </div>

          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)', padding: '20px',
          }}>
            <Icon name='lock' size='huge' color='grey' style={{ marginBottom: '20px' }} />
            <Header as='h3' style={{ marginBottom: '10px', textAlign: 'center' }}>
              {t('gameDetailedChat.joinRequired.title') || 'Join the game to view chat'}
            </Header>
            <p style={{ color: '#767676', textAlign: 'center', maxWidth: '90%', width: '400px', marginBottom: '20px' }}>
              {t('gameDetailedChat.joinRequired.message') || 'Chat is only available to game participants. Join the game to see messages and chat with other players.'}
            </p>
            <Message info style={{ maxWidth: '90%', width: '400px', margin: '0 auto' }}>
              <Message.Header>
                {t('gameDetailedChat.joinRequired.infoTitle') || 'Why join?'}
              </Message.Header>
              <p style={{ marginBottom: 0 }}>
                {t('gameDetailedChat.joinRequired.infoMessage') || 'Connect with other players, coordinate meeting times, and discuss game details.'}
              </p>
            </Message>
          </div>
        </Segment>
      </>
    );
  }

  return (
    <>
      <Segment textAlign='center' attached='top' inverted color='teal' style={{ border: 'none' }}>
        <Header>{t('gameDetailedChat.title')}</Header>
      </Segment>

      <Segment attached clearing>
        {game.isGameInThePast && (
          <p style={{ color: 'gray', marginBottom: '1em', fontStyle: 'italic' }}>
            {t('gameDetailedChat.gameFinishedMessage')}
          </p>
        )}

        {!game.isGameInThePast && (
          <Formik
            onSubmit={(values, { resetForm }) =>
              commentStore.addComment(values).then(() => resetForm())
            }
            initialValues={{ comment: '' }}
            validationSchema={Yup.object({
              comment: Yup.string().required().max(COMMENT_MAX_LENGTH),
            })}
          >
            {({ isSubmitting, isValid, handleSubmit, values, setFieldValue }) => (
              <Form className='ui form'>
                <Field name='comment'>
                  {(props: FieldProps) => (
                    <div style={{ position: 'relative' }}>
                      <Loader active={isSubmitting} />
                      <textarea
                        placeholder={t('gameDetailedChat.commentPlaceholder')}
                        rows={2}
                        {...props.field}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val.length <= COMMENT_MAX_LENGTH) {
                            setFieldValue('comment', val);
                          }
                        }}
                        onKeyUp={e => {
                          if (e.key === 'Enter' && e.shiftKey) return;
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            isValid && handleSubmit();
                          }
                        }}
                        style={{
                          borderRadius: '5px',
                          padding: '10px',
                          paddingBottom: '24px',
                          fontSize: '14px',
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        bottom: 6,
                        right: 8,
                        fontSize: '0.72em',
                        color: values.comment.length > COMMENT_MAX_LENGTH - 100 ? '#db2828' : '#aaa',
                        pointerEvents: 'none',
                      }}>
                        {values.comment.length}/{COMMENT_MAX_LENGTH}
                      </span>
                    </div>
                  )}
                </Field>
              </Form>
            )}
          </Formik>
        )}

        <Comment.Group style={{ marginTop: '20px' }}>
          {commentStore.comments.map(comment => (
            <Comment key={comment.id} style={{ padding: '10px', marginBottom: '10px', borderRadius: '5px', backgroundColor: '#f9f9f9' }}>
              <Comment.Avatar src={comment.image || '/assets/user.png'} />
              <Comment.Content>
                <Comment.Author as={Link} to={`/profiles/${comment.userID}`}>
                  {comment.fullName}
                </Comment.Author>
                <Comment.Metadata>
                  <div>{t('gameDetailedChat.timeAgo', {
                    time: i18n.language === 'srLatn'
                      ? convertToLatinScript(formatDistanceToNow(comment.createdAt, { locale: getDateLocale(i18n.language) }))
                      : formatDistanceToNow(comment.createdAt, { locale: getDateLocale(i18n.language) })
                  })}</div>
                </Comment.Metadata>
                <Comment.Text style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>
                  {comment.comment}
                </Comment.Text>

                {!game.isGameInThePast && userStore.user?.userID === comment.userID && (
                  <Comment.Actions>
                    <Button
                      size='mini'
                      compact
                      color='red'
                      basic
                      onClick={() => handleDeleteInitiated(comment.id)}
                      style={{ padding: '5px 10px', borderRadius: '3px', fontSize: '12px', marginTop: '5px' }}
                    >
                      <Icon name='trash' /> {t('gameDetailedChat.deleteButton')}
                    </Button>
                  </Comment.Actions>
                )}
              </Comment.Content>
            </Comment>
          ))}
        </Comment.Group>

        <Modal size='mini' open={confirmOpen} onClose={() => setConfirmOpen(false)} dimmer='blurring'>
          <Modal.Header style={{ backgroundColor: '#f8f8f9', borderBottom: '1px solid #d4d4d5' }}>
            {t('gameDetailedChat.deleteModal.title')}
          </Modal.Header>
          <Modal.Content>
            <p>{t('gameDetailedChat.deleteModal.message')}</p>
            {deletingComment && (
              <div style={{ textAlign: 'center', margin: '15px 0' }}>
                <Loader active inline size='small' />
                <span style={{ marginLeft: '10px', color: '#767676' }}>{t('gameDetailedChat.deleteModal.deletingMessage')}</span>
              </div>
            )}
          </Modal.Content>
          <Modal.Actions style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #d4d4d5' }}>
            <Button basic onClick={() => setConfirmOpen(false)} disabled={deletingComment}>
              {t('gameDetailedChat.deleteModal.cancelButton')}
            </Button>
            <Button
              negative
              content={t('gameDetailedChat.deleteModal.confirmButton')}
              icon='trash'
              labelPosition='right'
              onClick={handleDeleteConfirmed}
              loading={deletingComment}
              disabled={deletingComment}
            />
          </Modal.Actions>
        </Modal>
      </Segment>
    </>
  );
});